import { useEffect, useRef, useCallback } from 'react';
import { API } from '../config/api';
import { useAuth } from '../context/AuthContext';

/**
 * useQualifiedView — fire-and-forget hook that submits a qualified-view attempt.
 *
 * Usage:
 *   const { reportProgress } = useQualifiedView({ contentId, contentType, durationSeconds });
 *   // In your audio/video player's `timeupdate` handler:
 *   reportProgress({ secondsWatched: media.currentTime, isVisible: !document.hidden, isAudible: !media.muted });
 *
 * The hook only submits once per mount (at the moment thresholds are hit), so it's cheap.
 */
export default function useQualifiedView({ contentId, contentType, durationSeconds }) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const submittedRef = useRef(false);
  const sessionIdRef = useRef(`sess_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`);

  const reportProgress = useCallback(async ({ secondsWatched, isVisible = true, isAudible = true }) => {
    if (submittedRef.current) return;
    if (!isAuthenticated) return; // Guests are never qualified
    if (!contentId || !contentType) return;

    // Client-side pre-check to avoid firing before thresholds
    const percent = durationSeconds > 0 ? (secondsWatched / durationSeconds) * 100 : 0;
    if (secondsWatched < 30 && percent < 25) return;

    submittedRef.current = true; // lock immediately to avoid double-fires
    try {
      const res = await fetch(`${API}/views/qualify`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_id: contentId,
          content_type: contentType,
          seconds_watched: Number(secondsWatched.toFixed(2)),
          duration_seconds: durationSeconds || null,
          percent_watched: percent,
          is_visible: !!isVisible,
          is_audible: !!isAudible,
          session_id: sessionIdRef.current,
        }),
      });
      if (!res.ok) {
        // Unlock so we can retry if server rejected for a transient reason
        submittedRef.current = false;
      }
    } catch {
      submittedRef.current = false;
    }
  }, [contentId, contentType, durationSeconds, getAuthHeaders, isAuthenticated]);

  // Reset lock on content change
  useEffect(() => {
    submittedRef.current = false;
  }, [contentId]);

  return { reportProgress };
}
